import React, { useState, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MemberSpotlight = () => {
  const [currentSpotlight, setCurrentSpotlight] = useState(0);

  const spotlights = [
    {
      id: 1,
      name: "Thilini Amarasinghe",
      profession: "Environmental Engineer",
      company: "Ceylon Electricity Board",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=500&h=500&fit=crop&crop=face",
      achievement: "Green Energy Champion",
      month: "January 2025",
      story: `Thilini led our most ambitious environmental project to date - the installation of solar panels in 15 rural schools across the Western Province. Her technical expertise and project management skills resulted in a 60% reduction in electricity costs for these schools, saving over LKR 2.5 million annually.\n\nBeyond the financial impact, Thilini's initiative has become a model for sustainable development in education. She personally trained 45 teachers on renewable energy concepts, creating a ripple effect of environmental awareness that now reaches over 3,000 students.`,
      impact: {
        metric: "2.5M LKR",
        description: "Annual savings generated",
        details: "15 schools equipped with solar panels"
      },
      quote: "Engineering solutions for community problems is what drives me. Leo Circle gave me the platform to turn technical knowledge into meaningful social impact.",
      projectImages: [
        "https://images.pexels.com/photos/9875415/pexels-photo-9875415.jpeg?w=400&h=300&fit=crop",
        "https://images.pexels.com/photos/9875416/pexels-photo-9875416.jpeg?w=400&h=300&fit=crop"
      ],
      skills: ["Project Management", "Renewable Energy", "Community Engagement"],
      recognition: "Outstanding Service Award 2024"
    },
    {
      id: 2,
      name: "Ashan Wickramaratne",
      profession: "Software Developer",
      company: "99X Technology",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=500&h=500&fit=crop&crop=face",
      achievement: "Digital Innovation Leader",
      month: "December 2024",
      story: `Ashan revolutionized how we connect with our community by developing the Leo Circle mobile app, which now serves over 5,000 users. The app features event registration, volunteer matching, and impact tracking - increasing member engagement by 150%.\n\nHis coding bootcamp for underprivileged youth has graduated 3 cohorts, with 85% of participants securing employment in the tech industry. Ashan's approach combines technical training with soft skills development, ensuring holistic career preparation.`,
      impact: {
        metric: "150%",
        description: "Increase in member engagement",
        details: "5,000+ app users and 3 bootcamp cohorts"
      },
      quote: "Technology should bridge gaps, not create them. Every line of code I write for Leo Circle is aimed at making service more accessible and impactful.",
      projectImages: [
        "https://images.pexels.com/photos/3861969/pexels-photo-3861969.jpeg?w=400&h=300&fit=crop",
        "https://images.pexels.com/photos/3861972/pexels-photo-3861972.jpeg?w=400&h=300&fit=crop"
      ],
      skills: ["Mobile Development", "Training & Mentorship", "Digital Strategy"],
      recognition: "Innovation Excellence Award 2024"
    },
    {
      id: 3,
      name: "Sachini Perera",
      profession: "Clinical Psychologist",
      company: "Mind Wellness Center",
      image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=500&h=500&fit=crop&crop=face",
      achievement: "Mental Health Advocate",
      month: "November 2024",
      story: `Sachini pioneered our mental health awareness campaign, reaching over 10,000 young people across Colombo. Her workshops on stress management and emotional wellness have become the most requested programs in our service portfolio.\n\nRecognizing the stigma around mental health in Sri Lankan society, Sachini created culturally sensitive resources and trained 25 Leo members as peer counselors. Her approach has resulted in a 40% increase in young people seeking mental health support.`,
      impact: {
        metric: "10,000+",
        description: "Young people reached",
        details: "25 peer counselors trained"
      },
      quote: "Mental health is not a luxury - it's a necessity. Through Leo Circle, I've learned that healing happens in community, and service is therapeutic for both giver and receiver.",
      projectImages: [
        "https://images.pexels.com/photos/7176319/pexels-photo-7176319.jpeg?w=400&h=300&fit=crop",
        "https://images.pexels.com/photos/7176320/pexels-photo-7176320.jpeg?w=400&h=300&fit=crop"
      ],
      skills: ["Mental Health Advocacy", "Workshop Facilitation", "Peer Training"],
      recognition: "Community Impact Award 2024"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSpotlight((prev) => (prev + 1) % spotlights.length);
    }, 10000); // Change every 10 seconds

    return () => clearInterval(interval);
  }, [spotlights.length]);

  const currentMember = spotlights[currentSpotlight];

  const nextSpotlight = () => {
    setCurrentSpotlight((prev) => (prev + 1) % spotlights.length);
  };

  const prevSpotlight = () => {
    setCurrentSpotlight((prev) => (prev - 1 + spotlights.length) % spotlights.length);
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Member Spotlight
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Each month, we celebrate a member whose exceptional service and leadership 
            exemplifies the Leo Circle spirit of excellence.
          </p>
        </div>

        <div className="bg-card rounded-2xl card-shadow overflow-hidden">
          {/* Header with Navigation */}
          <div className="bg-gradient-to-r from-primary to-primary/80 text-white p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-white/20 rounded-lg p-2">
                  <Icon name="Star" size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">Spotlight: {currentMember.month}</h3>
                  <p className="text-white/80">{currentMember.achievement}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={prevSpotlight}
                  className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-smooth"
                >
                  <Icon name="ChevronLeft" size={20} className="text-white" />
                </button>
                <button
                  onClick={nextSpotlight}
                  className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-smooth"
                >
                  <Icon name="ChevronRight" size={20} className="text-white" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Member Profile */}
              <div className="lg:col-span-1">
                <div className="text-center mb-6">
                  <div className="w-32 h-32 mx-auto mb-4 overflow-hidden rounded-full">
                    <Image
                      src={currentMember.image}
                      alt={currentMember.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h4 className="text-2xl font-bold text-foreground mb-2">{currentMember.name}</h4>
                  <p className="text-primary font-medium mb-1">{currentMember.profession}</p>
                  <p className="text-muted-foreground text-sm mb-4">{currentMember.company}</p>
                  
                  <div className="bg-accent/10 text-accent px-4 py-2 rounded-lg inline-block mb-4">
                    <Icon name="Award" size={16} className="inline mr-2" />
                    {currentMember.recognition}
                  </div>
                </div>

                {/* Skills */}
                <div className="mb-6">
                  <h5 className="font-bold text-foreground mb-3">Key Skills</h5>
                  <div className="space-y-2">
                    {currentMember.skills.map((skill, index) => (
                      <div key={index} className="flex items-center">
                        <Icon name="CheckCircle" size={16} className="text-accent mr-2" />
                        <span className="text-sm text-muted-foreground">{skill}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Impact Metric */}
                <div className="bg-surface rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-primary mb-1">{currentMember.impact.metric}</div>
                  <div className="text-sm font-medium text-foreground mb-1">{currentMember.impact.description}</div>
                  <div className="text-xs text-muted-foreground">{currentMember.impact.details}</div>
                </div>
              </div>

              {/* Story Content */}
              <div className="lg:col-span-2">
                <div className="mb-6">
                  <h5 className="text-xl font-bold text-foreground mb-4">Impact Story</h5>
                  <div className="prose prose-gray max-w-none">
                    {currentMember.story.split('\n\n').map((paragraph, index) => (
                      <p key={index} className="text-muted-foreground mb-4 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>

                {/* Quote */}
                <blockquote className="bg-muted rounded-lg p-6 mb-6">
                  <Icon name="Quote" size={24} className="text-primary mb-3" />
                  <p className="text-foreground italic mb-3">"{currentMember.quote}"</p>
                  <cite className="text-sm text-muted-foreground">— {currentMember.name}</cite>
                </blockquote>

                {/* Project Images */}
                <div className="mb-6">
                  <h5 className="font-bold text-foreground mb-3">Project Gallery</h5>
                  <div className="grid grid-cols-2 gap-4">
                    {currentMember.projectImages.map((image, index) => (
                      <div key={index} className="aspect-video overflow-hidden rounded-lg">
                        <Image
                          src={image}
                          alt={`Project image ${index + 1}`}
                          className="w-full h-full object-cover hover:scale-105 transition-smooth"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button variant="default" className="hero-gradient">
                    <Icon name="MessageCircle" size={16} className="mr-2" />
                    Connect with {currentMember.name.split(' ')[0]}
                  </Button>
                  <Button variant="outline">
                    <Icon name="ExternalLink" size={16} className="mr-2" />
                    View Full Project
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicators */}
          <div className="px-8 pb-6">
            <div className="flex justify-center space-x-2">
              {spotlights.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSpotlight(index)}
                  className={`w-3 h-3 rounded-full transition-smooth ${
                    index === currentSpotlight ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MemberSpotlight;